import type { ComponentType } from 'react'
import { createBrowserRouter, Navigate } from 'react-router'
import { Landing } from './pages/landing/Landing'
import { Login } from './pages/Login'
import { LoginConfirm } from './pages/LoginConfirm'
import { NotFound } from './pages/NotFound'
import { RouteError } from './pages/RouteError'
import { Loading } from './components/ui'

// The landing page and login load eagerly; the dashboard is split into its own chunks.
const page = <T,>(load: () => Promise<T>, pick: (m: T) => ComponentType) => async () => ({ Component: pick(await load()) })

export const router = createBrowserRouter([
  { path: '/', element: <Landing />, errorElement: <RouteError /> },
  { path: '/login', element: <Login />, errorElement: <RouteError /> },
  { path: '/login/confirm', element: <LoginConfirm />, errorElement: <RouteError /> },
  { path: '/data-deletion', lazy: page(() => import('./pages/DataDeletion'), (m) => m.DataDeletion), errorElement: <RouteError /> },
  { path: '/privacy', lazy: page(() => import('./pages/legal/Privacy'), (m) => m.Privacy), errorElement: <RouteError /> },
  { path: '/terms', lazy: page(() => import('./pages/legal/Terms'), (m) => m.Terms), errorElement: <RouteError /> },
  { path: '/dpa', lazy: page(() => import('./pages/legal/Dpa'), (m) => m.Dpa), errorElement: <RouteError /> },
  { path: '/imprint', lazy: page(() => import('./pages/legal/Imprint'), (m) => m.Imprint), errorElement: <RouteError /> },
  {
    path: '/app',
    lazy: page(() => import('./pages/AppGate'), (m) => m.AppGate),
    hydrateFallbackElement: <Loading />,
    errorElement: <RouteError />,
    children: [
      { index: true, lazy: page(() => import('./pages/AppGate'), (m) => m.AppIndex) },
      { path: 'setup', lazy: page(() => import('./pages/setup/SetupWizard'), (m) => m.SetupWizard) },
      { path: 'operator', lazy: page(() => import('./pages/operator/OperatorPage'), (m) => m.OperatorPage) },
      {
        lazy: page(() => import('./components/AppShell'), (m) => m.AppShell),
        children: [
          { path: 'today', lazy: page(() => import('./pages/app/Today'), (m) => m.Today) },
          { path: 'conversations', lazy: page(() => import('./pages/app/Conversations'), (m) => m.Conversations) },
          { path: 'conversations/:conversationId', lazy: page(() => import('./pages/app/Conversations'), (m) => m.Conversations) },
          { path: 'profile', lazy: page(() => import('./pages/app/BusinessProfile'), (m) => m.BusinessProfile) },
          { path: 'training', lazy: page(() => import('./pages/app/Training'), (m) => m.Training) },
          { path: 'situations', lazy: page(() => import('./pages/app/PastSituations'), (m) => m.PastSituations) },
          { path: 'jobs', lazy: page(() => import('./pages/app/Jobs'), (m) => m.Jobs) },
          { path: 'integrations', lazy: page(() => import('./pages/app/Integrations'), (m) => m.Integrations) },
          { path: 'channels', lazy: page(() => import('./pages/app/ChannelsPage'), (m) => m.ChannelsPage) },
          { path: 'whatsapp', element: <Navigate to="/app/channels" replace /> },
          { path: 'plan', lazy: page(() => import('./pages/app/PlanUsage'), (m) => m.PlanUsage) },
          { path: '*', element: <Navigate to="/app" replace /> },
        ],
      },
    ],
  },
  { path: '*', element: <NotFound /> },
])
